import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  listMedia, getMediaConfig, getMediaUrl, formatBytes,
  type MediaAsset, type MediaConfig,
} from '../../lib/media';
import { ApiError } from '../../lib/apiClient';

/**
 * Choose a media asset for a block prop.
 *
 * ── THE PROP HOLDS AN ID, NEVER A URL ────────────────────────────────────────────────
 *
 * A document stores the asset's id. The URL is derived at render time, so an asset that
 * moves bucket, becomes private, or is replaced does not leave a published page pointing
 * at a stale address — and so no document can name an arbitrary external URL, which is
 * how a CMS becomes a way to embed someone else's server in your page.
 *
 * ── IT REFUSES BEFORE THE SERVER HAS TO ──────────────────────────────────────────────
 *
 * The list is filtered to the kind the slot wants and to assets that are ready, because
 * the publish gate rejects the alternatives and finding that out at Publish is finding it
 * out too late. The filtering here is a courtesy; validateMediaReferences on the engine
 * is the rule, and it re-checks kind, status, and ownership regardless of what was sent.
 *
 * ── AN UNCONFIGURED ENVIRONMENT SAYS SO ──────────────────────────────────────────────
 *
 * Object storage is not configured on every environment. When it is not, this says that
 * plainly and stays usable for clearing a reference — it does not present an empty
 * library as though the organization had simply never uploaded anything.
 */
export function MediaPicker({
  value, mediaKind, organizationId, scope, onChange, onClose,
}: {
  value: string | null;
  mediaKind: 'image' | 'video';
  /** The owning organization of the template or page being edited; null for platform. */
  organizationId: string | null;
  scope: 'platform' | 'organization';
  onChange: (assetId: string | null) => void;
  onClose: () => void;
}) {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [config, setConfig] = useState<MediaConfig | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listMedia({ organizationId, kind: mediaKind, status: 'ready' }),
      getMediaConfig().catch(() => null),
    ])
      .then(([rows, cfg]) => {
        if (cancelled) return;
        setAssets(rows);
        setConfig(cfg);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'The media library could not be loaded.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [organizationId, mediaKind]);

  // A platform template renders for every organization, so it may only use platform
  // media. Showing a tenant's files here would be offering a choice the publish gate
  // refuses, which is the definition of a dead end.
  const selectable = useMemo(() => assets.filter((a) => {
    if (scope === 'platform' && a.scope !== 'platform') return false;
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return a.displayName.toLowerCase().includes(needle)
      || (a.originalFilename ?? '').toLowerCase().includes(needle);
  }), [assets, scope, search]);

  return (
    <div className="cms-modal" role="dialog" aria-modal="true" aria-label={`Choose ${mediaKind}`}>
      <div className="cms-modal__panel cms-modal__panel--wide">
        <header className="cms-modal__head">
          <h2>Choose {mediaKind === 'video' ? 'a video' : 'an image'}</h2>
          <button type="button" className="button button--ghost adm-btn-sm" onClick={onClose}>Close</button>
        </header>

        {config && !config.configured && (
          <p className="alert alert--warning" role="alert">
            Object storage is not configured on this environment, so there is no media
            library to choose from. The block will fall back to the event&rsquo;s own
            artwork, which is what it does today.
          </p>
        )}

        <label className="field">
          <span className="field__label">Search</span>
          <input className="field__input" value={search} placeholder="Name or filename"
            onChange={(e) => setSearch(e.target.value)} />
        </label>

        {error && <p className="alert alert--error" role="alert">{error}</p>}
        {loading && <p className="adm-muted">Loading the library…</p>}

        {!loading && !error && selectable.length === 0 && (
          <p className="adm-muted">
            {scope === 'platform'
              ? 'No platform-owned ' : 'No '}
            {mediaKind === 'video' ? 'videos' : 'images'} are available here yet. Upload
            one in Content &amp; design &rarr; Media.
          </p>
        )}

        <ul className="cms-picker">
          {selectable.map((asset) => (
            <li key={asset.id}>
              <button
                type="button"
                className={`cms-picker__item${value === asset.id ? ' is-selected' : ''}`}
                onClick={() => { onChange(asset.id); onClose(); }}
              >
                <MediaThumb asset={asset} />
                <span className="cms-picker__name">{asset.displayName}</span>
                <span className="cms-picker__meta">
                  {asset.mimeType}
                  {asset.byteSize ? ` · ${formatBytes(asset.byteSize)}` : ''}
                  {asset.scope === 'platform' ? ' · Platform' : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <footer className="cms-modal__foot">
          <button type="button" className="button button--ghost"
            onClick={() => { onChange(null); onClose(); }}>
            Clear this slot
          </button>
        </footer>
      </div>
    </div>
  );
}

/** A preview for public images only — signing a link per row would sign links nobody asked for. */
function MediaThumb({ asset }: { asset: MediaAsset }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (asset.kind !== 'image' || asset.visibility !== 'public') return;
    let cancelled = false;
    getMediaUrl(asset.id).then((r) => { if (!cancelled) setUrl(r.url); }).catch(() => {});
    return () => { cancelled = true; };
  }, [asset.id, asset.kind, asset.visibility]);

  if (url) return <img className="cms-picker__thumb" src={url} alt="" loading="lazy" />;
  return (
    <span className="cms-picker__thumb cms-picker__thumb--icon" aria-hidden="true">
      {asset.kind === 'video' ? '▶' : '🖼'}
    </span>
  );
}

/**
 * The read-only face of a media prop: what is selected, and what happens when it is not.
 *
 * Separate from the picker because the inspector shows this on every render and opens the
 * picker only on request.
 */
export function MediaSlot({
  assetId, emptyNote, onOpen,
}: { assetId: string | null; emptyNote: string; onOpen: () => void }) {
  const [asset, setAsset] = useState<MediaAsset | null>(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(() => {
    if (!assetId) { setAsset(null); setMissing(false); return; }
    let cancelled = false;
    // Looked up by id through the library listing rather than a per-asset endpoint, so a
    // reference to an asset this admin cannot see reads as missing rather than as an
    // error — which is also what the publish gate will say about it.
    listMedia({ status: 'ready' })
      .then((rows) => {
        if (cancelled) return;
        const found = rows.find((a) => a.id === assetId) ?? null;
        setAsset(found);
        setMissing(!found);
      })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [assetId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="cms-slot">
      {assetId && asset && <MediaThumb asset={asset} />}
      <div className="cms-slot__text">
        {!assetId && <span className="adm-muted">{emptyNote}</span>}
        {assetId && asset && <span>{asset.displayName}</span>}
        {assetId && missing && (
          <span className="cms-slot__missing">
            This block points at a file that is no longer available. Publishing will be
            refused until it is replaced or cleared.
          </span>
        )}
      </div>
      <button type="button" className="button button--ghost adm-btn-sm" onClick={onOpen}>
        {assetId ? 'Change' : 'Choose'}
      </button>
    </div>
  );
}
